import { Link } from 'react-router-dom';

import { LegalEmail, LegalHeading, LEGAL_ENTITY, legalEntityDescription, LegalLayout } from './LegalLayout';
import { Note } from '../../components/ui/Note';
import { BILLING_LIVE, BILLING_PENDING_LEGAL_NOTE, PAID_PLAN_TRIAL, PAID_PLAN_TRIAL_DAYS } from '../../config';
import { PLAN_CURRENCY } from '../../data/plans';

export function TermsPage() {
    return (
        <LegalLayout title="Terms of service">
            <p>
                These terms cover your use of the ActualSold API, operated by {legalEntityDescription()}. Using the
                service means you accept them. Our registration details and full registered address are available on
                request at <LegalEmail />.
            </p>

            <LegalHeading>What the service is</LegalHeading>
            <p>
                ActualSold is a marketplace-data API. A request returns publicly visible eBay listing data: completed
                sales, active listings, category browse, and a specific seller's sales, as JSON, over REST or an MCP
                server. We return rows; interpreting them is up to you or the model you've connected.
            </p>
            <p>
                We are not affiliated with, endorsed by, or connected to eBay Inc. or any other company named anywhere
                on this site. Product names belong to their owners.
            </p>
            <p>
                We fetch pages that eBay makes publicly visible, and we're responsible for how we fetch them. You're
                responsible for what you do with what comes back, including your own compliance with eBay's terms, and
                with any other rules that apply to your use.
            </p>

            <LegalHeading>Accounts and API keys</LegalHeading>
            <p>
                An account is created the first time you sign in with an email address. There's no separate signup
                step. Sign-in is by emailed link, so keep access to that mailbox; anyone who can read it can sign in as
                you.
            </p>
            <p>
                An API key is a credential, not an account. Keys are shown once at creation and stored by us only as a
                hash. If a key leaks, revoke it from the console immediately and issue a new one. A leaked key cannot
                be used to create further keys.
            </p>

            <LegalHeading>Plans and payment</LegalHeading>
            {!BILLING_LIVE ? <Note>{BILLING_PENDING_LEGAL_NOTE}</Note> : null}
            <p>
                Free accounts get 100 requests a month at no cost, no card required. Paid plans (Starter, Growth, Scale)
                are billed monthly in {PLAN_CURRENCY} through Stripe on a calendar-month cycle; we never see or store
                your card details. Your bank may convert and charge a foreign transaction fee. That is between you and
                them, and is not something we add or receive. Every plan is capped at 60 requests per minute.
                Cancellation is covered on the{' '}
                <Link to="/refunds" className="text-primary underline">
                    cancellation and refunds
                </Link>{' '}
                page, which forms part of these terms.
            </p>
            {PAID_PLAN_TRIAL ? (
                <p>
                    Eligible signed-in Free accounts may start one {PAID_PLAN_TRIAL_DAYS}-day free trial on a paid plan
                    at first upgrade. Free itself is not a timed trial. Trial rules, who qualifies, and what happens if
                    you cancel before the first charge are on the cancellation and refunds page.
                </p>
            ) : null}
            <p>
                Commercial use is permitted on every tier, including free. What you build on top of the API is yours:
                analysis, pricing tools, dashboards, anything with our rows underneath it. What you can't do is
                repackage the raw responses themselves as a data feed, dataset or API for other people. If that's the
                product you want to build, email us; it's a licensing conversation and not automatically a no.
            </p>

            <LegalHeading>Fair use</LegalHeading>
            <p>
                Requests are metered against your monthly quota and rate limit, both shown in response headers. Don't
                try to get around them, share credentials across accounts to multiply a free tier, or automate around
                the rate limit. If you need more volume than a plan offers, ask; that's a pricing conversation, not a
                violation.
            </p>

            <LegalHeading>What we do and don't promise</LegalHeading>
            <p>
                We aim for the API to be reliably available and to return real data from the pages we fetch, not
                fabricated or stale rows. We do not promise the data is complete, that eBay's own figures agree with
                ours, or that anything returned predicts what will sell. Decisions you make on top of this data are
                yours.
            </p>
            <p>
                The service is provided as is, without warranty, and our total liability is limited to what you have
                paid us in the previous twelve months. Availability depends on continued access to publicly visible eBay
                pages, and that could change without warning. If it does, paid subscribers can cancel and stop paying.
            </p>

            {/*
             * Notice, not uptime. We control when we email; we do not control
             * whether eBay keeps rendering a field, which is why the forced-change
             * case is carved out rather than promised against. An SLA here would
             * be the unbacked claim docs/competitor-soldcomps.md §8 says not to
             * copy from SoldComps.
             */}
            <LegalHeading>Changes to the API</LegalHeading>
            <p>
                Fields get added. When we add one we do it without notice. A new key in a JSON object doesn't break
                code that isn't looking for it.
            </p>
            <p>
                Removing or renaming a field, or changing what an existing one means, is different, and we'll email
                active subscribers at least 30 days before it takes effect. The exception is a change eBay forces on us:
                if a page stops carrying something, we can't keep returning it, and you'll hear as soon as we know
                rather than 30 days beforehand.
            </p>
            <p>
                Endpoints follow the same rule. <code className="font-mono text-xs">/v1</code> will not disappear
                underneath a working integration without that notice.
            </p>

            <LegalHeading>Ending it</LegalHeading>
            <p>
                Cancelling a plan and closing an account are two different things. Cancelling stops the next payment and
                drops you to Free at the end of the period you've paid for. The account, its keys and its history stay.
                Closing removes the account itself.
            </p>
            <p>
                You can close your account at any time by emailing <LegalEmail /> from the address you sign in with.
                We'll delete your account, your API keys and everything we hold against them, and confirm when it's
                done. Cancel any paid plan first, or ask us to do both in the same email. Deletion is not reversible and
                we can't recover a closed account. Signing in again with the same address starts a new, empty one. What
                we have to keep afterwards for tax and accounting is set out on the{' '}
                <Link to="/privacy" className="text-primary underline">
                    privacy
                </Link>{' '}
                page.
            </p>
            <p>
                Rows you already pulled are yours to keep. Cancelling a plan or closing your account doesn't retract
                them, and there's nothing to delete on your side. The only limit is the one above. What you keep, you
                still can't resell as raw data.
            </p>
            <p>
                We may suspend or revoke access for an account that breaks these terms, doesn't pay, or is abusing the
                service.
            </p>

            {/*
             * The carve-back in the second sentence is load-bearing. An indemnity
             * without it reads as making the customer liable for our own product,
             * which is both unfair and the kind of clause a reader discounts
             * entirely, taking the rest of the page down with it.
             */}
            <LegalHeading>If your use causes us a problem</LegalHeading>
            <p>
                If someone brings a claim against us because of how you used the API, what you built, who you gave the
                data to, or a rule you were bound by and we weren't, that's yours to deal with, including reasonable
                legal costs. This doesn't apply to claims about the service itself, which stay ours.
            </p>

            {/* "Changes to these terms", not "Changes", there is now a "Changes
             * to the API" section above it and the two are different promises. */}
            <LegalHeading>Changes to these terms</LegalHeading>
            <p>
                We may update these terms. Material changes will be emailed to active subscribers before they take
                effect, and continuing to use the service after that means accepting them.
            </p>

            <LegalHeading>Governing law</LegalHeading>
            <p>
                These terms are governed by the laws of {LEGAL_ENTITY.governingLaw}, and disputes are heard in the
                courts of {LEGAL_ENTITY.courts}.
            </p>

            <LegalHeading>Contact</LegalHeading>
            <p>
                <LegalEmail />
            </p>
        </LegalLayout>
    );
}
